// Catalog.ID Client — Recovery Kit Status Line
// ============================================================================
// One sentence describing the kit on record, plus the 12-month fire-drill
// reminder (Recovery Kit Addendum §14). Shared by the Settings pane (personal
// and org-as-active-identity kits) and the org workspace's Recovery Kit pane,
// so the two never drift apart on wording or on when the reminder fires.

import { createElement } from '@catalog-shell';
import type { RecoveryKitInfo } from '../api/account';

/** A kit untested for this long earns the reminder. */
const TEST_REMINDER_MS = 365 * 24 * 3600 * 1000;

/** Pure: whether the reminder is due, given the kit's dates and "now". */
export function isKitTestOverdue(kit: RecoveryKitInfo, now: number = Date.now()): boolean {
    const reference = new Date(kit.lastTestedAt ?? kit.createdAt).getTime();
    if (!Number.isFinite(reference)) return false;
    return now - reference > TEST_REMINDER_MS;
}

/** Pure: the status sentence for a kit on record. */
export function describeRecoveryKit(kit: RecoveryKitInfo): string {
    const created = new Date(kit.createdAt).toLocaleDateString();
    const tested = kit.lastTestedAt ? new Date(kit.lastTestedAt).toLocaleDateString() : 'never';
    return `Kit ${kit.recoverySetId.slice(0, 8)}… — ${kit.thresholdK} of ${kit.totalSharesN} `
        + `sheet types, created ${created}, last tested ${tested}.`;
}

/**
 * Append the status sentence — and, when the kit is overdue for a fire drill,
 * the reminder — to `target`.
 *
 * @param emptyMessage what to say when there is no kit on record; the personal
 *        and organization surfaces word this differently.
 */
export function appendRecoveryKitStatus(
    target: HTMLElement,
    kit: RecoveryKitInfo | null,
    emptyMessage: string,
): void {
    target.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: kit ? describeRecoveryKit(kit) : emptyMessage,
    }));

    if (kit && isKitTestOverdue(kit)) {
        target.appendChild(createElement('p', {
            className: 'cid-wizard-warning',
            textContent: 'It has been over 12 months since this kit was last tested. Verify that at least '
                + 'one encrypted key backup is reachable and that your sheets are still readable.',
        }));
    }
}
